import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { API_HEADER, DIALOG_TYPES, IS_API_SIGNAL } from '../constants';
import { BadgesService } from '../modules/badges/badges.service';
import { ApiService } from '../services/api.service';
import { ClientService } from '../services/client.service';
import { DataShareService } from '../services/data-share.service';
import { WaitErrorDialogsService } from '../services/wait-error-dialogs.service';
import { sendCoralogixCustomEvent } from '../utils/coralogix.util';

const cancelPendingHttpRequests$ = new Subject<void>();

export function interceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const clientService = inject(ClientService);
  const badgesService = inject(BadgesService);
  const weds = inject(WaitErrorDialogsService);
  const apiService = inject(ApiService);
  const dataShare = inject(DataShareService);
  const router = inject(Router);

  const body = request.method === 'GET' ? 'GET' : request.body ? JSON.stringify(request.body) : null;
  const url = request.url.split(apiService.url)?.[1] || request.url;

  if (apiService.cache.has(url) && apiService.cache.get(url).has(body)) {
    return toObservable(signal(new HttpResponse({ status: 200, statusText: 'OK', body: apiService.cache.get(url).get(body) })));
  }

  const isPrismApi = request.url.includes(apiService.url);
  const isAdminApi = dataShare.url().includes('/admin') || dataShare.url().includes('%2Fadmin');
  const isGlobalAdmin = clientService.isGlobalAdmin();
  const currentAdminClientId = localStorage.getItem('currentAdminClientId');
  const requiresCredentials = request.url.includes('/s3/') && !request.url.includes('X-Amz-Algorithm');
  const isCrossOriginS3 = requiresCredentials && typeof window !== 'undefined' && request.url.startsWith('http') && new URL(request.url).origin !== window.location.origin && !request.url.includes('ngsw-bypass');
  const authRequest = request.clone({
    ...(requiresCredentials ? { withCredentials: true } : {}),
    ...(isCrossOriginS3 ? { url: `${request.url}${request.url.includes('?') ? '&' : '?'}ngsw-bypass=true` } : {}),
    setHeaders: {
      ...(isPrismApi && isAdminApi && isGlobalAdmin && currentAdminClientId ? { [API_HEADER.X_ASSUMED_CLIENT_ID]: currentAdminClientId } : {}),
    }
  });

  return next(authRequest).pipe(
    takeUntil(cancelPendingHttpRequests$),
    tap(result => {
      if (result?.['body']?.['status'] === 'Logout') {
        clientService.logout();
      }
      // Handle user engagement data
      const userEngagement = result?.['body']?.['userEngagement'];
      if (userEngagement?.['data']) {
        const userEngagementData = userEngagement['data'];
        if (userEngagementData?.['badges']?.length > 0) {
          badgesService.handleImmediateBadgeUpdate(userEngagementData['badges']);
        }
      } else if (userEngagement?.['error']) {
        console.error(userEngagement['error']);
      }
    }),
    catchError((requestError: HttpErrorResponse) => {
      if (requestError?.status === 403) {
        handleAccessDenied(router, weds);
      } else if (request.context.get(IS_API_SIGNAL)) {
        if (requestError?.status === 401 || requestError?.status === 402) {
          clientService.logout();
        }
      } else {
        handleApiFailure(requestError, clientService, weds);
      }

      return throwError(() => requestError);
    })
  );
}

function handleAccessDenied(router: Router, weds: WaitErrorDialogsService): void {
  cancelPendingHttpRequests$.next();
  weds.closeDialog();
  router.navigate(['/accessDenied']);
}

function handleApiFailure(error: HttpErrorResponse, clientService: ClientService, weds: WaitErrorDialogsService): void {
  switch (error?.status) {
    case 400:
      weds.showDialog({ type: DIALOG_TYPES.GENERIC, title: 'Error', message: error.error?.message ? error.error?.message : 'Something went wrong, Please try again later!', isApiFailureDialog: true });
      break;
    case 401:
    case 402:
      clientService.logout();
      break;
    default: {
      sendCoralogixCustomEvent('OOPS error logged (unhandled HTTP status)', {
        error: {
          status: error?.status,
          statusText: error?.statusText,
          message: error?.message,
          name: error?.name,
          url: error?.url,
          body: error?.error,
        },
      });
      weds.showDialog({
        type: DIALOG_TYPES.GENERIC,
        code: 0,
        ...(error.error.error?.description && { message: error.error.error?.description, title: 'Error!' }),
        dontCloseAllDialogs: true,
        isApiFailureDialog: true,
      });
      break;
    }
  }
}
